/**
 * thumbnail-utils.ts — N-tier track / artist thumbnail fallback chain
 *
 * Priority order (a track / work):
 *  1. Track's own coverArtUrl / imageUrl
 *  2. Release cover art        (coverMap: workId → coverArtUrl)
 *  3. Artist artwork           (artistThumbMap: artistId → artists/{id}.imageUrl)
 *  4. Creator profile image    (userThumbMap: userId → profileImageUrl)
 *  5. Generated vector + text  (deterministic initials SVG, data: URL)
 *
 * The generated SVG is the GUARANTEED last-resort — every entity always has
 * artwork to show. See generateInitialsSvgDataUrl().
 */

import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, documentId } from "firebase/firestore";

// ─── Build helpers ────────────────────────────────────────────────────────────

/**
 * Builds a release-cover reverse-map from a list of release docs.
 * Each track workId maps to the release's coverArtUrl.
 *
 * Works with Firestore release docs that have:
 *   { coverArtUrl?: string; tracks?: Array<{ workId?: string; id?: string }> }
 */
export function buildReleaseCoverMap(releases: any[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const rel of releases) {
    const cover = rel.coverArtUrl || rel.imageUrl || "";
    if (!cover) continue;
    // Map by release doc id
    if (rel.id && !map.has(rel.id)) map.set(rel.id, cover);
    if (rel.releaseId && rel.releaseId !== rel.id && !map.has(rel.releaseId))
      map.set(rel.releaseId, cover);
    // Map each linked track workId
    if (Array.isArray(rel.tracks)) {
      for (const t of rel.tracks) {
        const wid = t.workId || t.id;
        if (wid && !map.has(wid)) map.set(wid, cover);
      }
    }
  }
  return map;
}

/**
 * Batch-fetches user profile thumbnails for a set of user IDs.
 * Returns a Map<userId, profileImageUrl>.
 *
 * Uses Firestore documentId() 'in' queries (max 30 per batch).
 */
export async function buildUserThumbMap(
  userIds: string[]
): Promise<Map<string, string>> {
  const unique = [...new Set(userIds.filter(Boolean))];
  if (unique.length === 0) return new Map();

  const map = new Map<string, string>();
  const BATCH = 30; // Firestore 'in' limit

  const fetches: Promise<void>[] = [];
  for (let i = 0; i < unique.length; i += BATCH) {
    const batch = unique.slice(i, i + BATCH);
    fetches.push(
      getDocs(
        query(collection(db, "users"), where(documentId(), "in", batch))
      )
        .then((snap) => {
          snap.forEach((d) => {
            const data = d.data() as {
              profileImageUrl?: string;
              imageUrl?: string;
              photoURL?: string;
            };
            const thumb =
              data.profileImageUrl || data.imageUrl || data.photoURL;
            if (thumb) map.set(d.id, thumb);
          });
        })
        .catch(() => {})
    );
  }
  await Promise.all(fetches);
  return map;
}

/**
 * Batch-fetches artist artwork (`artists/{id}.imageUrl`) for a set of artist IDs.
 * Returns a Map<artistId, imageUrl>.
 */
export async function buildArtistThumbMap(
  artistIds: string[]
): Promise<Map<string, string>> {
  const unique = [...new Set(artistIds.filter(Boolean))];
  if (unique.length === 0) return new Map();

  const map = new Map<string, string>();
  const BATCH = 30;
  const fetches: Promise<void>[] = [];
  for (let i = 0; i < unique.length; i += BATCH) {
    const batch = unique.slice(i, i + BATCH);
    fetches.push(
      getDocs(
        query(collection(db, "artists"), where(documentId(), "in", batch))
      )
        .then((snap) => {
          snap.forEach((d) => {
            const data = d.data() as {
              imageUrl?: string;
              coverArtUrl?: string;
              thumbnailUrl?: string;
            };
            const thumb = data.imageUrl || data.coverArtUrl || data.thumbnailUrl;
            if (thumb) map.set(d.id, thumb);
          });
        })
        .catch(() => {})
    );
  }
  await Promise.all(fetches);
  return map;
}

// ─── Generated fallback art ──────────────────────────────────────────────────

/**
 * Deterministic hash of a string → 0..2^32-1. Tiny FNV-1a. Used to pick a
 * gradient palette so the same name always gets the same colours.
 */
function fnv1a(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Returns 2 hex colours (start, end) derived from `seed`. */
function gradientFromSeed(seed: string): [string, string] {
  const h = fnv1a(seed);
  // Two hues 60-120° apart on the colour wheel, deep saturation
  const h1 = h % 360;
  const h2 = (h1 + 60 + (h >>> 8) % 60) % 360;
  return [
    `hsl(${h1} 70% 38%)`,
    `hsl(${h2} 78% 18%)`,
  ];
}

/** Returns up to 2 initials from a free-form name (e.g. "Don Skills" → "DS"). */
export function initialsOf(name: string): string {
  const cleaned = (name || "").trim().replace(/[^\p{L}\p{N}\s]/gu, "");
  if (!cleaned) return "BB";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Generate a square SVG thumbnail (data: URL) with a name-seeded gradient and
 * the entity's initials. Use as a last-resort fallback when no real artwork
 * exists. Cheap, deterministic, no network, works server- and client-side.
 *
 * Size defaults to 512px. Pass any size; SVG scales cleanly.
 */
export function generateInitialsSvgDataUrl(
  name: string,
  size: number = 512
): string {
  const initials = initialsOf(name);
  const [c1, c2] = gradientFromSeed(name || "BB");
  const fontSize = Math.round(size * 0.42);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${c1}"/>
      <stop offset="100%" stop-color="${c2}"/>
    </linearGradient>
  </defs>
  <rect width="${size}" height="${size}" fill="url(#g)"/>
  <text x="50%" y="50%" text-anchor="middle" dominant-baseline="central"
        font-family="Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif"
        font-weight="800" font-size="${fontSize}" fill="rgba(255,255,255,0.95)"
        letter-spacing="-2">${initials}</text>
</svg>`;
  // Use base64 so the data URL survives in any context (Next/Image, <img>, CSS bg)
  const encoded = typeof window === "undefined"
    ? Buffer.from(svg, "utf8").toString("base64")
    : btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${encoded}`;
}

// ─── Resolver ─────────────────────────────────────────────────────────────────

/**
 * Resolves a track's display thumbnail using the full fallback chain. Never
 * returns an empty string — falls all the way through to a generated SVG.
 *
 * @param track          Any track/work object (loose shape)
 * @param coverMap       release reverse-map from buildReleaseCoverMap()
 * @param userThumbMap   user thumb map from buildUserThumbMap() (optional)
 * @param artistThumbMap artist thumb map from buildArtistThumbMap() (optional)
 */
export function resolveTrackThumb(
  track: {
    id?: string;
    workId?: string;
    coverArtUrl?: string;
    imageUrl?: string;
    title?: string;
    artist?: string;
    owner?: { userId?: string };
    artistId?: string;
    uploadedBy?: string;
    createdByUid?: string;
  },
  coverMap: Map<string, string>,
  userThumbMap: Map<string, string> = new Map(),
  artistThumbMap: Map<string, string> = new Map()
): string {
  // Tier 1 — track's own image
  if (track.coverArtUrl) return track.coverArtUrl;
  if (track.imageUrl) return track.imageUrl;

  // Tier 2 — release cover
  const id = track.id || track.workId || "";
  const releaseThumb = (id && coverMap.get(id)) || "";
  if (releaseThumb) return releaseThumb;

  // Tier 3 — artist artwork
  if (track.artistId) {
    const artistThumb = artistThumbMap.get(track.artistId);
    if (artistThumb) return artistThumb;
  }

  // Tier 4 — creator's profile image. NEVER track.artistId here — that's an
  // artists/{id} doc id (already tried against artistThumbMap in Tier 3),
  // not a users/{uid} id; feeding it into userThumbMap let an unrelated
  // person's profile photo win whenever an artist doc id happened to
  // collide with a real user's uid.
  const userId =
    track.owner?.userId ||
    track.uploadedBy ||
    track.createdByUid ||
    "";
  if (userId) {
    const creatorThumb = userThumbMap.get(userId);
    if (creatorThumb) return creatorThumb;
  }

  // Tier 5 — generated initials SVG (guaranteed non-empty)
  return generateInitialsSvgDataUrl(track.title || track.artist || "Track");
}

/**
 * Same fallback chain as resolveTrackThumb but tells you WHICH tier matched,
 * so admin tooling can flag "this track needs its own artwork".
 */
export function resolveTrackThumbWithTier(
  track: Parameters<typeof resolveTrackThumb>[0],
  coverMap: Map<string, string>,
  userThumbMap: Map<string, string> = new Map(),
  artistThumbMap: Map<string, string> = new Map()
): { url: string; tier: 1 | 2 | 3 | 4 | 5 } {
  if (track.coverArtUrl) return { url: track.coverArtUrl, tier: 1 };
  if (track.imageUrl)    return { url: track.imageUrl,    tier: 1 };

  const id = track.id || track.workId || "";
  const releaseThumb = (id && coverMap.get(id)) || "";
  if (releaseThumb) return { url: releaseThumb, tier: 2 };

  if (track.artistId) {
    const artistThumb = artistThumbMap.get(track.artistId);
    if (artistThumb) return { url: artistThumb, tier: 3 };
  }

  // See resolveTrackThumb above — track.artistId must never feed userThumbMap.
  const userId =
    track.owner?.userId ||
    track.uploadedBy || track.createdByUid || "";
  if (userId) {
    const creatorThumb = userThumbMap.get(userId);
    if (creatorThumb) return { url: creatorThumb, tier: 4 };
  }

  return { url: generateInitialsSvgDataUrl(track.title || track.artist || "Track"), tier: 5 };
}

/**
 * Resolves an artist's thumbnail. If `imageUrl` / `coverArtUrl` exist, use them;
 * otherwise generate a deterministic initials SVG from the artist name.
 */
export function resolveArtistThumb(artist: {
  imageUrl?: string;
  coverArtUrl?: string;
  thumbnailUrl?: string;
  name?: string;
  slug?: string;
}): string {
  return (
    artist.imageUrl ||
    artist.coverArtUrl ||
    artist.thumbnailUrl ||
    generateInitialsSvgDataUrl(artist.name || artist.slug || "Artist")
  );
}
